import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const KrishnaButterBall = () => {
  const [gameState, setGameState] = useState('menu'); // menu, playing, paused, gameOver
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [krishnaPosition, setKrishnaPosition] = useState({ x: 50, y: 80 });
  const [butterBalls, setButterBalls] = useState([]);
  const [obstacles, setObstacles] = useState([]);
  const [level, setLevel] = useState(1);
  const [highScore, setHighScore] = useState(localStorage.getItem('krishnaButterBallHighScore') || 0);
  const [gameSpeed, setGameSpeed] = useState(2);
  const [collectedButter, setCollectedButter] = useState(0);
  const [showStory, setShowStory] = useState(false);

  // Krishna stories for different levels
  const krishnaStories = [
    {
      level: 1,
      title: "Little Krishna's Mischievous Nature",
      story: "Baby Krishna was extremely mischievous. He would steal butter from pots hung high in homes, earning him the name 'Makhan Chor' (Butter Thief). His charm made everyone forgive his pranks!",
      image: "/images/avatars/krishna.png"
    },
    {
      level: 2,
      title: "Krishna and the Yamunâ",
      story: "When Krishna was young, he purified the Yamuna river by defeating the serpent Kâliya. The river became safe for people and animals to bathe in.",
      image: "/images/avatars/krishna.png"
    },
    {
      level: 3,
      title: "Krishna's Divine Leelas",
      story: "Krishna performed many divine acts (leelas) that taught people about dharma (righteousness) and bhakti (devotion). His playful nature concealed deep spiritual wisdom.",
      image: "/images/avatars/krishna.png"
    }
  ];

  // Initialize game
  const initGame = useCallback(() => {
    setScore(0);
    setLives(3);
    setKrishnaPosition({ x: 50, y: 80 });
    setButterBalls([]);
    setObstacles([]);
    setLevel(1);
    setGameSpeed(2);
    setCollectedButter(0);
    setGameState('playing');
  }, []);

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameState !== 'playing') return;
      
      const moveAmount = 5;
      setKrishnaPosition(prev => {
        let newX = prev.x;
        let newY = prev.y;
        
        switch(e.key) {
          case 'ArrowLeft':
            newX = Math.max(5, prev.x - moveAmount);
            break;
          case 'ArrowRight':
            newX = Math.min(95, prev.x + moveAmount);
            break;
          case 'ArrowUp':
            newY = Math.max(10, prev.y - moveAmount);
            break;
          case 'ArrowDown':
            newY = Math.min(90, prev.y + moveAmount);
            break;
          default:
            return prev;
        }
        
        return { x: newX, y: newY };
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState]);

  // Handle touch/mouse controls
  const handleAreaClick = (e) => {
    if (gameState !== 'playing') return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    // Only move if click is not too close to current position
    if (Math.abs(x - krishnaPosition.x) > 5 || Math.abs(y - krishnaPosition.y) > 5) {
      setKrishnaPosition({
        x: Math.max(5, Math.min(95, x)),
        y: Math.max(10, Math.min(90, y))
      });
    }
  };

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const gameLoop = setInterval(() => {
      // Generate butter balls
      if (Math.random() < 0.3) {
        const newButterBall = {
          id: Date.now(),
          x: Math.random() * 90 + 5,
          y: -5,
          type: 'butter'
        };
        setButterBalls(prev => [...prev, newButterBall]);
      }
      
      // Generate obstacles (milk pots that break if hit)
      if (Math.random() < 0.1 + level * 0.05) {
        const newObstacle = {
          id: Date.now() + 1,
          x: Math.random() * 90 + 5,
          y: -5,
          type: 'pot'
        };
        setObstacles(prev => [...prev, newObstacle]);
      }
      
      // Move butter balls and obstacles
      setButterBalls(prev => 
        prev.map(item => ({
          ...item,
          y: item.y + gameSpeed
        })).filter(item => item.y < 100)
      );
      
      setObstacles(prev => 
        prev.map(item => ({
          ...item,
          y: item.y + gameSpeed
        })).filter(item => item.y < 100)
      );
      
      // Check collisions
      // Check butter ball collection
      setButterBalls(prev => {
        const remaining = [];
        prev.forEach(ball => {
          const distance = Math.sqrt(
            Math.pow(ball.x - krishnaPosition.x, 2) + 
            Math.pow(ball.y - krishnaPosition.y, 2)
          );
          
          if (distance < 8) {
            // Collected
            setScore(s => s + 10);
            setCollectedButter(c => c + 1);
            
            // Level up every 10 butter balls
            if (collectedButter + 1 >= level * 10) {
              setLevel(l => l + 1);
              setGameSpeed(s => s + 0.5);
              setShowStory(true);
            }
          } else {
            remaining.push(ball);
          }
        });
        return remaining;
      });
      
      // Check obstacle collisions
      setObstacles(prev => {
        const remaining = [];
        prev.forEach(obstacle => {
          const distance = Math.sqrt(
            Math.pow(obstacle.x - krishnaPosition.x, 2) + 
            Math.pow(obstacle.y - krishnaPosition.y, 2)
          );
          
          if (distance < 10) {
            // Hit obstacle
            setLives(l => {
              const newLives = l - 1;
              if (newLives <= 0) {
                setGameState('gameOver');
                if (score > highScore) {
                  setHighScore(score);
                  localStorage.setItem('krishnaButterBallHighScore', score);
                }
              }
              return newLives;
            });
          } else {
            remaining.push(obstacle);
          }
        });
        return remaining;
      });
    }, 100);
    
    return () => clearInterval(gameLoop);
  }, [gameState, krishnaPosition, gameSpeed, level, collectedButter, score, highScore]);

  // Touch controls for mobile
  const handleTouchMove = (e) => {
    if (gameState !== 'playing') return;
    
    e.preventDefault();
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    
    setKrishnaPosition({
      x: Math.max(5, Math.min(95, x)),
      y: Math.max(10, Math.min(90, y))
    });
  };

  const currentStory = krishnaStories.find(story => story.level === level) || krishnaStories[0];

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-3xl md:text-4xl font-bold text-center mb-2 text-red-500">Krishna's Butter Ball Challenge</h1>
      <p className="text-center text-lg mb-6 text-gray-600">Help baby Krishna collect butter balls while avoiding broken pots!</p>
      
      {/* Game Stats */}
      <div className="flex justify-between items-center mb-4 bg-yellow-100 rounded-full px-6 py-3 shadow-md">
        <div className="font-bold text-red-500">Score: {score}</div>
        <div className="font-bold text-red-500">Level: {level}</div>
        <div className="font-bold text-red-500">Lives: {'❤️'.repeat(lives)}</div>
        <div className="font-bold text-red-500">High Score: {highScore}</div>
      </div>
      
      {/* Game Area */}
      <div 
        className="relative w-full h-96 bg-gradient-to-b from-sky-200 to-green-200 rounded-2xl shadow-xl overflow-hidden border-4 border-yellow-400 cursor-pointer"
        onClick={handleAreaClick}
        onTouchMove={handleTouchMove}
      >
        {/* Background elements */}
        <div className="absolute top-4 left-4 w-16 h-16 rounded-full bg-yellow-100 opacity-30"></div>
        <div className="absolute top-10 right-8 w-12 h-12 rounded-full bg-yellow-100 opacity-30"></div>
        <div className="absolute bottom-20 left-10 w-20 h-20 rounded-full bg-yellow-100 opacity-30"></div>
        
        {/* Ground */}
        <div className="absolute bottom-0 w-full h-16 bg-gradient-to-t from-green-600 to-green-400"></div>
        
        {/* Krishna */}
        <AnimatePresence>
          {gameState === 'playing' && (
            <motion.div
              className="absolute w-16 h-16 flex items-center justify-center"
              style={{
                left: `${krishnaPosition.x}%`,
                top: `${krishnaPosition.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
              animate={{ 
                scale: [1, 1.1, 1],
              }}
              transition={{ 
                duration: 1,
                repeat: Infinity 
              }}
            >
              <img 
                src="/images/avatars/krishna.png" 
                alt="Krishna" 
                className="w-12 h-12 rounded-full object-cover border-2 border-blue-500"
                onError={(e) => {
                  e.target.src = '/images/placeholder-avatar.jpg';
                }}
              />
              <div className="absolute -bottom-2 w-16 h-4 bg-yellow-300 rounded-full"></div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Butter Balls */}
        <AnimatePresence>
          {butterBalls.map(ball => (
            <motion.div
              key={ball.id}
              className="absolute w-8 h-8 rounded-full bg-yellow-300 border-2 border-yellow-500 flex items-center justify-center"
              style={{
                left: `${ball.x}%`,
                top: `${ball.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <div className="w-4 h-4 rounded-full bg-yellow-100"></div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {/* Obstacles (Broken Pots) */}
        <AnimatePresence>
          {obstacles.map(obstacle => (
            <motion.div
              key={obstacle.id}
              className="absolute w-10 h-10 flex items-center justify-center"
              style={{
                left: `${obstacle.x}%`,
                top: `${obstacle.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: 360 }}
              exit={{ scale: 0 }}
            >
              <div className="w-8 h-8 bg-gray-500 rounded-sm relative">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-4 bg-gray-600"></div>
                <div className="absolute bottom-0 left-0 w-full h-2 bg-gray-700"></div>
                <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full"></div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {/* Game Over Screen */}
        {gameState === 'gameOver' && (
          <motion.div 
            className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center text-white p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h2 className="text-4xl font-bold mb-4 text-red-400">Game Over!</h2>
            <p className="text-2xl mb-2">Final Score: {score}</p>
            <p className="text-xl mb-6">Butter Balls Collected: {collectedButter}</p>
            {score > highScore && (
              <p className="text-xl mb-6 text-yellow-300 animate-pulse">New High Score! 🎉</p>
            )}
            <div className="flex gap-4">
              <button 
                onClick={initGame}
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-full transition-colors"
              >
                Play Again
              </button>
              <button 
                onClick={() => setGameState('menu')}
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-full transition-colors"
              >
                Main Menu
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Menu Screen */}
        {gameState === 'menu' && (
          <motion.div 
            className="absolute inset-0 bg-gradient-to-b from-blue-400 to-purple-600 flex flex-col items-center justify-center text-white p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h2 className="text-4xl font-bold mb-6 text-yellow-300">Krishna's Butter Ball Challenge</h2>
            <div className="bg-white bg-opacity-20 rounded-2xl p-6 mb-6 max-w-md">
              <h3 className="text-2xl font-bold mb-3 text-center">How to Play</h3>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li>Move Krishna with arrow keys or by clicking/touching</li>
                <li>Collect yellow butter balls for points</li>
                <li>Avoid broken pots (they reduce lives)</li>
                <li>Collect 10 butter balls to advance to next level</li>
                <li>Complete all 3 levels to win!</li>
              </ul>
              <p className="text-center font-bold">Use arrow keys or touch to move Krishna!</p>
            </div>
            <button 
              onClick={initGame}
              className="bg-yellow-500 hover:bg-yellow-600 text-red-700 font-bold py-3 px-8 rounded-full text-xl transition-colors shadow-lg"
            >
              Start Game
            </button>
          </motion.div>
        )}
        
        {/* Story Modal */}
        <AnimatePresence>
          {showStory && (
            <motion.div
              className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-2xl p-6 max-w-md w-full"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold text-red-600">{currentStory.title}</h3>
                  <button 
                    onClick={() => setShowStory(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
                <div className="mb-4 flex items-center justify-center">
                  <img 
                    src={currentStory.image} 
                    alt="Krishna" 
                    className="w-24 h-24 rounded-full object-cover border-4 border-blue-500"
                    onError={(e) => {
                      e.target.src = '/images/placeholder-avatar.jpg';
                    }}
                  />
                </div>
                <p className="text-gray-700 mb-4">{currentStory.story}</p>
                <div className="text-center text-sm text-gray-500 mb-4">
                  Level {level} Complete! Keep collecting butter balls!
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowStory(false)}
                    className="bg-red-600 text-white px-6 py-2 rounded-full hover:bg-red-700 transition-colors"
                  >
                    Continue Playing
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Game Controls */}
      <div className="mt-6 grid grid-cols-3 gap-4 max-w-xs mx-auto">
        <div></div>
        <button 
          className="bg-red-500 text-white p-4 rounded-full shadow-md flex items-center justify-center"
          onClick={() => {
            if (gameState === 'playing') {
              setKrishnaPosition(prev => ({
                ...prev,
                y: Math.max(10, prev.y - 10)
              }));
            }
          }}
        >
          ↑
        </button>
        <div></div>
        
        <button 
          className="bg-red-500 text-white p-4 rounded-full shadow-md flex items-center justify-center"
          onClick={() => {
            if (gameState === 'playing') {
              setKrishnaPosition(prev => ({
                ...prev,
                x: Math.max(5, prev.x - 10)
              }));
            }
          }}
        >
          ←
        </button>
        <button 
          className={`p-4 rounded-full shadow-md flex items-center justify-center ${
            gameState === 'playing' ? 'bg-yellow-500 text-red-700' : 'bg-green-500 text-white'
          }`}
          onClick={() => {
            if (gameState === 'playing') {
              setGameState('paused');
            } else if (gameState === 'paused') {
              setGameState('playing');
            } else if (gameState === 'menu' || gameState === 'gameOver') {
              initGame();
            }
          }}
        >
          {gameState === 'playing' ? '⏸️' : gameState === 'paused' ? '▶️' : '🔄'}
        </button>
        <button 
          className="bg-red-500 text-white p-4 rounded-full shadow-md flex items-center justify-center"
          onClick={() => {
            if (gameState === 'playing') {
              setKrishnaPosition(prev => ({
                ...prev,
                x: Math.min(95, prev.x + 10)
              }));
            }
          }}
        >
          →
        </button>
        
        <div></div>
        <button 
          className="bg-red-500 text-white p-4 rounded-full shadow-md flex items-center justify-center"
          onClick={() => {
            if (gameState === 'playing') {
              setKrishnaPosition(prev => ({
                ...prev,
                y: Math.min(90, prev.y + 10)
              }));
            }
          }}
        >
          ↓
        </button>
        <div></div>
      </div>
      
      {/* Game Info */}
      <div className="mt-8 bg-gradient-to-r from-red-100 to-yellow-100 rounded-2xl p-6">
        <h3 className="text-2xl font-bold text-red-600 mb-3">About Krishna's Childhood</h3>
        <p className="text-gray-700 mb-4">
          Krishna is one of the most beloved deities in Hinduism, known for his playful and mischievous childhood. 
          He was particularly famous for stealing butter from homes in Gokul, earning him the name "Makhan Chor" (Butter Thief). 
          These playful acts were not just pranks but had deeper spiritual meanings, teaching devotees about divine love and joy.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="bg-white bg-opacity-50 rounded-lg p-3">
            <h4 className="font-bold text-red-500">Level 1: Butter Thief</h4>
            <p className="text-sm">Help Krishna collect butter balls while avoiding obstacles</p>
          </div>
          <div className="bg-white bg-opacity-50 rounded-lg p-3">
            <h4 className="font-bold text-red-500">Level 2: River Savior</h4>
            <p className="text-sm">Navigate through more challenging obstacles</p>
          </div>
          <div className="bg-white bg-opacity-50 rounded-lg p-3">
            <h4 className="font-bold text-red-500">Level 3: Divine Leelas</h4>
            <p className="text-sm">Master the final challenge with increased speed</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KrishnaButterBall;